import AppStateService from '@ajs/services/AppStateService';
import FdxUI from '@ajs/services/fdxUI';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, input, Input, InputSignal, OnDestroy, OnInit, Output, Self } from '@angular/core';
import { FormGroup, FormGroupDirective } from '@angular/forms';
import { CountryDropdownOption } from '@app/analyze-data/constants/analyze-data.constants';
import { CategoryPathsUtilitiesService } from '@app/analyze-data/services/category-paths-utilities.service';
import { ChannelInputStateService } from '@app/analyze-data/services/channel-input-state.service';
import { ChannelOptionsType, ChannelRegionsType } from '@app/analyze-data/types/channel-options.type';
import { CategoryTaxonomyDataService } from '@app/category-taxonomy/services/category-taxonomy-data.service';
import { ChannelsWithRegionsResponse } from '@app/category-taxonomy/services/responses/channels-with-regions.response';
import { isUndefined } from '@app/core/functions/isUndefined';
import FdxError from '@app/core/models/classes/fdx-error.class';
import { ChannelClient } from '@app/core/models/enums/channel-client.enum';
import { BigCommerceModeStateService } from '@app/core/services/bigcommerce-mode-state.service';
import { CountryService } from '@app/core/services/country.service';
import { OrderByPipe } from '@app/modules/pipes/pipes/order-by/order-by.pipe';
import { TCountryCode, TLanguageCode } from 'countries-list';
import { Observable, Subject, filter, map, take, takeUntil, tap, throwError } from 'rxjs';


@Component({
    selector: 'fdx-channel-and-region-inputs',
    templateUrl: './channel-and-region-inputs.component.html',
    styleUrl: './channel-and-region-inputs.component.scss',
    providers: [
        ChannelInputStateService
    ],
    standalone: false
})
export class ChannelAndRegionInputsComponent implements OnInit, OnDestroy {

    databaseId: string = this.appStateService.getDatabaseId();

    @Input() form: FormGroupDirective;
    @Input() group: FormGroup;
    @Input() fieldClass: string | undefined = undefined;
    @Input() labelClass: string;
    @Input() inputClass: string;
    @Input() hideNoTaxonomy?: boolean;  // Temporary for hiding Google and Facebook from the list since they don't have taxonomy attributes yet
    /** Lists TaaS taxonomies in place of their legacy matches. Only for callers that can validate against TaaS. */
    readonly includeTaxonomyService: InputSignal<boolean> = input<boolean>(false);

    @Output() readonly loadedChannels: EventEmitter<void> = new EventEmitter<void>();
    @Output() readonly channelChange: EventEmitter<void> = new EventEmitter<void>();

    channels: ChannelOptionsType[] = [];
    countries: CountryDropdownOption[] = [];

    private readonly unsubscribe$: Subject<void> = new Subject<void>();

    constructor(
        @Self() private readonly channelInputStateService: ChannelInputStateService,
        private readonly appStateService: AppStateService,
        private readonly bigCommerceSelfServiceState: BigCommerceModeStateService,
        private readonly categoryPathsUtilitiesService: CategoryPathsUtilitiesService,
        private readonly categoryTaxonomyDataService: CategoryTaxonomyDataService,
        private readonly countryService: CountryService,
        private readonly fdxUI: FdxUI,
        private readonly orderByPipe: OrderByPipe
    ) {}

    ngOnInit(): void {
        this.initChannelsDropdown();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private initChannelsDropdown(): void {
        this.group.controls.channel.valueChanges.pipe(
            tap((channel: ChannelOptionsType) => this.updateCountries(channel)),
            takeUntil(this.unsubscribe$)
        ).subscribe();

        this.categoryTaxonomyDataService.getChannelsWithRegions(this.databaseId, this.includeTaxonomyService()).pipe(
            tap((response: ChannelsWithRegionsResponse) => {
                if (this.hideNoTaxonomy) {
                    /* Hide Google */
                    const googleIndex = response.data.findIndex((value) => value.destinations.length > 0 && value.destinations[0].client === ChannelClient.Google);
                    if (googleIndex > -1) {
                        response.data.splice(googleIndex, 1);
                    }
                    /* Hide Google */

                    /* Hide Facebook */
                    const facebookIndex = response.data.findIndex((value) => value.destinations.length > 0 && value.destinations[0].client === ChannelClient.Facebook);
                    if (facebookIndex > -1) {
                        response.data.splice(facebookIndex, 1);
                    }
                    /* Hide Facebook */
                }

                if (this.bigCommerceSelfServiceState.showRestrictedUI()) {
                    response.data = response.data.filter((channel: ChannelOptionsType) =>
                        this.categoryPathsUtilitiesService.isSurfaceChannelClient(channel.destinations[0]?.client)
                    );
                }

                this.channels = this.orderByPipe.transform(response.data, 'display');
                this.channelInputStateService.channels = this.channels;

                this.loadedChannels.emit();
            }),
            takeUntil(this.unsubscribe$)
        ).subscribe({
            error: (error: unknown) => {
                if (error instanceof HttpErrorResponse) {
                    this.fdxUI.showToastError(`Something went wrong loading channels: ${error.message}`);
                }
                return throwError(() => new FdxError('UNKNOWN_ERROR', 'Error loading channels'));
            }
        });
    }

    /**
     * Returns the channel from the channels array that matches the passed in channel name
     * @param newChannelName the name of the channel to try to find
     * @returns the matching channel type, or undefined if one is not found
     */
    findMatchingChannel(newChannelName: string): Observable<ChannelOptionsType | undefined> {
        return this.findChannel((channels: ChannelOptionsType[]) => channels.find(
            (channel: ChannelOptionsType) => channel.display.toLowerCase() === newChannelName.toLowerCase()
        ));
    }

    /**
     * Finds the TaaS channel for an export's `taxonomy_service_keys` entry (`{key}:{version}`).
     * Prefers the same version, then any version of the same key.
     */
    findTaxonomyServiceChannel(channelKey: string): Observable<ChannelOptionsType | undefined> {
        const wanted: { key: string; version: string; } = this.categoryPathsUtilitiesService.taxonomyServiceKeyParts(channelKey);

        return this.findChannel((channels: ChannelOptionsType[]) => {
            const sameKey: ChannelOptionsType[] = channels.filter((channel: ChannelOptionsType) => {
                const client: string | undefined = channel.destinations[0]?.client;
                return this.categoryPathsUtilitiesService.clientIsTaxonomyService(client)
                    && this.categoryPathsUtilitiesService.taxonomyServiceKeyParts(client).key === wanted.key;
            });

            return sameKey.find((channel: ChannelOptionsType) =>
                this.categoryPathsUtilitiesService.taxonomyServiceKeyParts(channel.destinations[0].client).version === wanted.version
            ) ?? sameKey[0];
        });
    }

    /** Finds the TaaS channel that replaced any of these legacy clients in the list. */
    findChannelReplacingLegacyClients(legacyClients: string[]): Observable<ChannelOptionsType | undefined> {
        return this.findChannel((channels: ChannelOptionsType[]) => channels.find(
            (channel: ChannelOptionsType) => channel.legacy_clients?.some((client: string) => legacyClients.includes(client))
        ));
    }

    /** Whether the channel offers a `{country}-{region}` value, the form the Categorization drawer writes to the URL. */
    hasCountry(channel: ChannelOptionsType, urlCountry: string): boolean {
        return channel.destinations.some((destination: ChannelRegionsType) =>
            `${this.countryCode(destination)}-${destination.region}`.toLowerCase() === urlCountry.toLowerCase()
        );
    }

    private findChannel(
        pick: (channels: ChannelOptionsType[]) => ChannelOptionsType | undefined
    ): Observable<ChannelOptionsType | undefined> {
        return this.channelInputStateService.channels$
            .pipe(
                filter((channels: ChannelOptionsType[] | null) => channels !== null),
                take(1),
                map(pick),
                takeUntil(this.unsubscribe$)
            );
    }

    /**
     * Update the countries dropdown/selection when we change channels
     * @param channel the channel we're updating to
     * @param urlCountry the country and region codes in the URL in the form of 'US-en-US', where the first 'US' is the country code, then 'en-US' is the region
     */
    updateCountries(channel: ChannelOptionsType, urlCountry: string = null): void {

        if (channel === null) {
            return;
        }

        // Clear out any old country selection when changing channels
        this.group.controls.country.reset();

        // Loop through countries to determine duplicates
        const countriesSeen = new Set<TCountryCode>();
        const duplicateCountries: TCountryCode[] = [];
        for (const destination of channel.destinations) {
            if (countriesSeen.has(destination.country)) {
                duplicateCountries.push(destination.country);
            }
            countriesSeen.add(destination.country);
        }

        // Map our regions to the country dropdown option type
        const newCountries = channel.destinations.map<CountryDropdownOption>((destination: ChannelRegionsType) => {
            destination.country = this.countryCode(destination);

            return {
                ...destination,
                countryName: this.countryService.getCountryNameByCode(destination.country),
                languageName: this.countryService.getLanguageNameByCode(destination.region.substring(0, 2) as TLanguageCode),
                displayLanguage: duplicateCountries.includes(destination.country)    // Only display the language in the UI if it is a duplicate country
            };
        });

        // Finally, order the new list by the name of the country
        this.countries = this.orderByPipe.transform(newCountries, 'countryName');
        const urlCountryIndex = urlCountry ? this.countries.findIndex((value: CountryDropdownOption) => `${value.country}-${value.region}`.toLowerCase() === urlCountry.toLowerCase()) : -1;
        const unitedStatesIndex = this.countries.findIndex((value: CountryDropdownOption) => value.countryName === 'United States');

        if (urlCountryIndex >= 0) {                 // Auto-select country if there's a matching one in the URL
            this.group.controls.country.setValue(this.countries[urlCountryIndex]);
        } else if (this.countries.length === 1) {   // Or select the first one if there's only one value (HTML hides input)
            this.group.controls.country.setValue(this.countries[0]);
        } else if (unitedStatesIndex >= 0) {        // Or set to the index of United States
            this.group.controls.country.setValue(this.countries[unitedStatesIndex]);
        }

        this.channelChange.emit();
    }

    /** Our library doesn't include the code 'UK', but Amazon actually uses 'UK' instead of the standard 'GB' code. */
    private countryCode(destination: ChannelRegionsType): TCountryCode {
        return String(destination.country) === 'UK' ? 'GB' : destination.country;
    }

    protected getFormFieldClasses(): Record<string, boolean> {

        const classes: Record<string, boolean> = { 'mb-2': true };

        if (!isUndefined(this.fieldClass)) {
            classes[this.fieldClass] = true;
        }

        return classes;

    }
}
